import { useEffect, useMemo, useState } from "react";
import { Coffee, X, Clock, PaperPlaneTilt, Chat } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

/**
 * Mini-widget on /pedidos that polls /api/breaks/open every 15s and lists the
 * workers currently on a task. Click a chip to open a detail modal with the
 * exact start time and a "Avisar por Telegram" button.
 */
export default function OpenBreaksWidget() {
  const [rows, setRows] = useState([]);
  const [tick, setTick] = useState(0);
  const [detail, setDetail] = useState(null); // selected break row
  const [sendingNotify, setSendingNotify] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/breaks/open");
        if (!alive) return;
        setRows(Array.isArray(r.data) ? r.data : []);
      } catch (_) {
        /* keep stale data */
      }
    };
    load();
    const dataT = setInterval(load, 15000);
    const clockT = setInterval(() => setTick((n) => n + 1), 30000);
    return () => { alive = false; clearInterval(dataT); clearInterval(clockT); };
  }, []);

  const items = useMemo(() => {
    return [...rows].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tick]);

  const notifyWorker = async () => {
    if (!detail) return;
    setSendingNotify(true);
    try {
      await api.post(`/breaks/${detail.id}/notify-reminder`);
      toast.success("Aviso enviado por Telegram");
      setDetail(null);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.info("La tarea ya se ha cerrado — refrescando lista.");
        setDetail(null);
        // Trigger an immediate refresh
        try {
          const r = await api.get("/breaks/open");
          setRows(Array.isArray(r.data) ? r.data : []);
        } catch (_) { /* ignore */ }
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSendingNotify(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-[var(--border)] p-4 mb-6 flex items-center gap-3" data-testid="open-breaks-widget-empty">
        <div className="w-9 h-9 rounded-xl bg-[var(--bg)] border border-[var(--border)] flex items-center justify-center">
          <Coffee size={18} weight="duotone" className="text-[var(--text-secondary)]" />
        </div>
        <div className="text-sm text-[var(--text-secondary)]">
          <span className="font-display font-semibold text-[var(--text-primary)]">Nadie en tarea.</span> Todo el equipo está disponible.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl bg-white border border-[var(--border)] p-4 mb-6" data-testid="open-breaks-widget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Coffee size={18} weight="duotone" className="text-amber-600" />
            <span className="label-cap">en tarea ahora</span>
            <span className="font-display font-bold text-sm">{items.length}</span>
          </div>
        </div>
        <ul className="flex flex-wrap gap-2">
          {items.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => setDetail(b)}
                data-testid={`open-break-${b.id}`}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-[var(--bg)] text-sm hover:bg-white transition cursor-pointer"
                style={{ borderColor: b.family_color || "#e5e7eb" }}
                title="Ver detalle"
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: b.family_color || "#94a3b8" }} />
                <span className="font-display font-semibold">{b.user_name}</span>
                <span className="text-[var(--text-secondary)]">·</span>
                <span>{b.reason}</span>
                <span className="font-mono text-xs text-[var(--text-secondary)]">{elapsed(b.start_time)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {detail && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !sendingNotify && setDetail(null)}
          data-testid="open-break-detail-dialog"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: detail.family_color || "#94a3b8" }}>
                  <Coffee size={22} weight="duotone" color="#fff" />
                </div>
                <div>
                  <div className="label-cap">en tarea</div>
                  <h3 className="font-display font-bold text-lg">{detail.user_name}</h3>
                  <div className="text-xs text-[var(--text-secondary)] capitalize">{detail.user_role}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetail(null)}
                disabled={sendingNotify}
                data-testid="open-break-detail-close"
                className="p-1 rounded hover:bg-[var(--bg)]"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div className="rounded-xl bg-[var(--bg)] border border-[var(--border)] p-3">
                <div className="label-cap mb-1">motivo</div>
                <div className="font-display font-semibold">
                  {detail.reason}
                  {detail.custom_reason && (
                    <span className="text-[var(--text-secondary)] font-normal"> — {detail.custom_reason}</span>
                  )}
                </div>
                {detail.family_name && (
                  <div className="text-xs text-[var(--text-secondary)] mt-1 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: detail.family_color }} />
                    {detail.family_name}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-[var(--bg)] border border-[var(--border)] p-3 flex items-center gap-3">
                <Clock size={18} weight="duotone" className="text-[var(--primary)]" />
                <div className="flex-1">
                  <div className="label-cap mb-0.5">inicio</div>
                  <div className="font-mono text-sm font-semibold" data-testid="open-break-detail-start">
                    {detail.start_time
                      ? new Date(detail.start_time).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
                      : "—"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="label-cap mb-0.5">lleva</div>
                  <div className="font-mono text-sm font-bold text-amber-700">{elapsed(detail.start_time)}</div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setDetail(null)}
                disabled={sendingNotify}
                className="flex-1 py-3 rounded-xl border border-[var(--border)] font-display font-semibold"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={notifyWorker}
                disabled={sendingNotify}
                data-testid="open-break-detail-notify"
                className="flex-1 py-3 rounded-xl font-display font-bold text-white bg-[var(--primary)] hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {sendingNotify ? <Chat size={16} weight="duotone" /> : <PaperPlaneTilt size={16} weight="fill" />}
                {sendingNotify ? "Enviando…" : "Avisar por Telegram"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function elapsed(iso) {
  if (!iso) return "";
  try {
    const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (diff < 60) return `${diff}m`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  } catch { return ""; }
}
